const BASE = import.meta.env.VITE_API_BASE ?? 'https://research.tpark.me/api/v1';

function getToken(): string {
  const t = localStorage.getItem('rhq_token');
  if (!t) throw new Error('TOKEN_MISSING');
  return t;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new Error(`${res.status} ${err?.code ?? 'UNKNOWN'}: ${err?.message ?? res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export type WatchlistItem = {
  symbol: string;
  name: string;
  active: boolean;
};

export type Report = {
  id: string;
  symbol: string;
  symbolName: string | null;
  broker: string | null;
  analyst: string | null;
  title: string;
  targetPrice: number | null;
  recommendation: string | null;
  targetPricePrev: number | null;
  targetPriceDeltaPct: number | null;
  recommendationPrev: string | null;
  publishedAt: string | null;
  collectedAt: string;
  status: string;
  // Phase C — batch 진행 상태 (Phase 3 batch path).
  batchName?: string | null;
  batchSubmittedAt?: string | null;
  // 가장 최근 vision_extract LLM call 의 model — quality 모니터링.
  model?: string | null;
};

export type WikiSection = {
  // quarter snapshot 에 없는 legacy 파생 필드는 optional (step4.2: 읽기 항상 quarter).
  id?: number;
  symbol: string;
  sectionKey: string;
  title: string;
  content: string;
  orderIdx: number;
  symbolVersion?: number;
  sectionVersion?: number;
  updatedAt?: string;
  lastSignificantChangeAt?: string | null;
  citations: unknown[];
  // v2.0 Step 3 — broker_views section 만. 다른 section 은 undefined.
  meta?: {
    d1_broker_count: number;
    d1_snapshot_count: number;
    wiki_broker_count: number;
  };
};

export type ReferencedChunk = {
  text: string;
  source_type: 'text' | 'vision';
  page_number: number | null;
  report_id: string;
  broker: string | null;
  title: string;
  published_at: string;
};

export type StockData = {
  market_cap_bn: number | null;
  foreign_pct: number | null;
  avg_volume_60d: number | null;
  shares_outstanding_mn: number | null;
  high_52w: number | null;
  low_52w: number | null;
  dated_at: string;
};

export type Risk = {
  text: string;
  severity: number;
  time_horizon: 'near' | 'mid' | 'long' | null;
  broker: string;
  dated_at: string;
};

export type FinancialRow = {
  broker: string;
  year: string;
  dated_at: string;
  revenue: number | null;
  op_income: number | null;
  op_margin: number | null;
  eps: number | null;
  per: number | null;
  pbr: number | null;
  bps: number | null;
  roe: number | null;
};

export type PeerRow = {
  peer_name: string;
  metric_type: string;
  our_value: number | null;
  peer_value: number | null;
  ratio: number | null;
  note: string | null;
  broker: string;
  dated_at: string;
};

export type OperationalRow = {
  period: string;
  metric_type: string;
  segment: string | null;
  value: number;
  unit: string;
  qoq_change: number | null;
  yoy_change: number | null;
  broker: string;
  dated_at: string;
};

export type Wiki = {
  symbol: string;
  name: string;
  symbol_version: number;
  quarter?: string | null; // PR4: 최신 servable 분기(quarter 모드). legacy 모드면 null/없음
  freshness?: 'updating' | 'failed' | null; // step2: 'updating'(갱신 중)·'failed'(갱신 실패)·null/없음(최신)
  model: string | null;
  generated_at: string | null;
  header: {
    consensus_target_price: { avg: number | null; min: number | null; max: number | null; count: number };
    rating_distribution: { buy: number; hold: number; sell: number; total: number };
  };
  // Phase v3.5a — Web UI surface
  stock_data: StockData | null;
  risks: Risk[];
  financials: FinancialRow[];
  peer_comparisons: PeerRow[];
  operational_metrics: OperationalRow[];
  sections: WikiSection[];
  referenced_chunks: Record<string, ReferencedChunk>;
};

export type FeedItem = {
  id: string;
  type: string;
  symbol: string;
  symbolName: string | null;
  occurred_at: string;
  collected_at: string | null;
  payload: Record<string, unknown>;
  source_report_id?: string | null;
  broker?: string | null;
};

export type AvailableReport = {
  externalId: string;
  source: 'naver';
  sourceUrl: string;
  pdfUrl: string | null;
  symbol: string;
  title: string;
  broker?: string;
  publishedAt: string;
  status: 'new' | 'already_ingested' | 'needs_broker_fetch';
  reportId?: string;
  /** broker fetch path 로 pdfUrl 채워진 경우 (현재 신한투자증권만). */
  pdfUrlSource?: 'shinhansec';
};

export type ReportsAvailable = {
  symbol: string;
  name: string;
  pages: number;
  total: number;
  entries: AvailableReport[];
};

export type EnqueueResult = {
  results: Array<{
    url: string;
    status: string;
    broker?: string | null;
    title?: string;
  }>;
};

export type CatalystSource = {
  reportId: string;
  broker: string | null;
  publishedAt: string;
};

export type Catalyst = {
  id: number;
  symbol: string;
  title: string;
  description: string | null;
  expectedDate: string | null;
  timeHorizon: 'near' | 'mid' | 'long' | null;
  direction: 'positive' | 'negative' | 'neutral' | null;
  conviction: number | null;
  status: 'pending' | 'occurred' | 'cancelled' | 'delayed' | null;
  occurredAt: string | null;
  dedupKey: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  sources: CatalystSource[];
};

export type AskResponse = {
  answer: string;
  model?: string;
  confidence?: number;
  uncertain: boolean;
  retrieval: {
    method: string;
    top_k: number;
    max_similarity: number;
    chunks_used: Array<{
      chunk_id: string;
      report_id?: string;
      report_title: string;
      broker: string | null;
      published_at?: string;
      page_number: number | null;
      similarity: number;
      snippet?: string;
    }>;
  };
  wiki_context_used?: string[];
  cost_usd: number;
  latency_ms: number;
  request_id?: string;
};

export type AskHistoryItem = {
  id: number;
  question: string;
  symbol: string | null;
  confidence: number | null;
  uncertain: boolean;
  filtered_reason: string | null;
  cost_usd: number | null;
  created_at: string;
};

// 상세 인용 — 서버가 타입 맞는 필드만 내려줌(allowlist+가드). 스니펫은 reports:text|ask:write 시만. 대부분 optional.
export type AskHistoryChunk = {
  chunk_id: string;
  report_id?: string;
  report_title?: string;
  broker?: string | null;
  published_at?: string;
  page_number?: number | null;
  similarity?: number;
  snippet?: string;
};

export type AskHistoryDetail = {
  id: number;
  question: string;
  symbol: string | null;
  answer: string;
  confidence: number | null;
  uncertain: boolean;
  filtered_reason: string | null;
  max_similarity: number | null;
  wiki_context_used: string[];
  chunks_used: AskHistoryChunk[];
  snippets_redacted: boolean;
  model: string | null;
  tokens: { input: number | null; output: number | null; cached: number | null };
  cost_usd: number | null;
  latency_ms: number | null;
  request_id: string | null;
  created_at: string;
};

export const api = {
  health: () =>
    fetch(`${BASE}/health`).then(
      (r) => r.json() as Promise<{ status: string; version: string; server_time: string }>,
    ),
  watchlist: () => request<{ items: WatchlistItem[] }>('/watchlist'),
  watchlistAdd: (symbol: string, name: string, naverItemNameEncoded?: string) =>
    request<WatchlistItem>('/watchlist', {
      method: 'POST',
      body: JSON.stringify({
        symbol,
        name,
        ...(naverItemNameEncoded ? { naverItemNameEncoded } : {}),
      }),
    }),
  watchlistToggle: (symbol: string, active: boolean) =>
    request<WatchlistItem>(`/watchlist/${encodeURIComponent(symbol)}`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),
  reports: (symbol?: string, status?: string, limit?: number) =>
    request<{ items: Report[]; next_cursor: string | null }>(
      `/reports?${new URLSearchParams({
        ...(symbol ? { symbol } : {}),
        ...(status ? { status } : {}),
        ...(limit ? { limit: String(limit) } : {}),
      }).toString()}`,
    ),
  report: (id: string) => request<Report>(`/reports/${id}`),
  /** R2-proxy PDF URL — 새 tab native viewer 로 열기 위해 `?token=` query 포함. */
  pdfUrl: (id: string) => `${BASE}/reports/${id}/pdf?token=${encodeURIComponent(getToken())}`,
  /** vision raw markdown (extracted_text 컬럼) — reports:text scope 필요. */
  reportText: (id: string) => request<{ id: string; text: string | null }>(`/reports/${id}/text`),
  wiki: (symbol: string) => request<Wiki>(`/wikis/${encodeURIComponent(symbol)}`),
  catalysts: (symbol: string) =>
    request<{ items: Catalyst[] }>(`/catalysts/${encodeURIComponent(symbol)}`),
  feed: (since?: string) => request<{ items: FeedItem[] }>(`/feed${since ? `?since=${since}` : ''}`),
  ask: (question: string, symbol?: string) =>
    request<AskResponse>('/ask', {
      method: 'POST',
      body: JSON.stringify({ question, ...(symbol ? { symbol } : {}) }),
    }),
  /** /ask Q&A 히스토리 리스트 (ask:read 또는 ask:write). 본문 제외 — 질문·종목·날짜·confidence·비용. */
  askHistory: (opts: { symbol?: string; limit?: number; offset?: number } = {}) =>
    request<{ items: AskHistoryItem[]; total: number; limit: number; offset: number }>(
      `/ask/history?${new URLSearchParams({
        ...(opts.symbol ? { symbol: opts.symbol } : {}),
        ...(opts.limit != null ? { limit: String(opts.limit) } : {}),
        ...(opts.offset != null ? { offset: String(opts.offset) } : {}),
      }).toString()}`,
    ),
  /** /ask Q&A 히스토리 상세 (ask:read 또는 ask:write). 스니펫은 reports:text|ask:write 보유 시만. */
  askHistoryDetail: (id: number) => request<AskHistoryDetail>(`/ask/history/${id}`),
  /** llm_runs 통계 — admin daily 모니터링. purpose 선택. */
  visionStats: (
    purpose:
      | 'vision_extract'
      | 'report_extract'
      | 'section_update'
      | 'quarter_projection'
      | 'grounded_rag_answer'
      | 'all' = 'vision_extract',
    window: '24h' | '7d' | '30d' | 'all' = '7d',
  ) =>
    request<{
      window: string;
      purpose: string;
      items: Array<{
        purpose: string;
        promptVersion: string;
        status: string;
        count: number;
        costUsd: number;
        avgInputTokens: number;
        avgOutputTokens: number;
        avgLatencyMs: number;
        lastRun: string;
      }>;
    }>(`/admin/vision-stats?purpose=${purpose}&window=${window}`),
  /** Phase B — admin settings 조회. */
  settings: () =>
    request<{
      batchVision: boolean;
      batchReportExtract: boolean;
      updatedAt: Record<string, string>;
    }>('/admin/settings'),
  /** Phase B — admin settings 부분 update. */
  patchSettings: (patch: {
    batchVision?: boolean;
    batchReportExtract?: boolean;
  }) =>
    request<{
      batchVision: boolean;
      batchReportExtract: boolean;
    }>('/admin/settings', { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Phase 2.5 — 네이버 listing 가용 보고서 (admin:write scope). */
  reportsAvailable: (symbol: string, pages: number) =>
    request<ReportsAvailable>(
      `/admin/reports-available?symbol=${encodeURIComponent(symbol)}&pages=${pages}`,
    ),
  /**
   * Phase 2.5 — 선택한 entries 직접 enqueue (admin:write scope).
   * items 는 list-available 응답을 그대로 forward — broker fetched PDF 포함.
   * force=true 시 재처리.
   * Phase 3 — processMode 'batch' (50% cost 절감, latency 수 분~수 시간) | 'realtime' (default).
   */
  enqueue: (
    items: Array<{
      sourceUrl: string;
      pdfUrl: string;
      externalId: string;
      source: string;
      symbol: string;
      title: string;
      broker?: string;
      analyst?: string;
      publishedAt: string;
    }>,
    force?: boolean,
    processMode?: 'batch' | 'realtime',
  ) =>
    request<{ results: Array<{ externalId: string; status: string }> }>('/admin/enqueue', {
      method: 'POST',
      body: JSON.stringify({
        items,
        ...(force ? { force: true } : {}),
        ...(processMode ? { processMode } : {}),
      }),
    }),
  /**
   * Phase 2.5 — 이미 수집된 reportId 들 재처리 (D1 reset + force enqueue).
   * already_ingested row 의 진짜 reprocess path — force enqueue 만으로는 workflow
   * 의 status early-exit 에 막혀 wiki regen 안 됨. regenerateVision=true 면 vision OCR 도 재실행.
   */
  regenerateWiki: (reportIds: string[], regenerateVision?: boolean) =>
    request<{ ok?: boolean; results?: Array<{ id?: string; status?: string }> }>(
      '/admin/regenerate-wiki',
      {
        method: 'POST',
        body: JSON.stringify({
          reportIds,
          ...(regenerateVision ? { regenerateVision: true } : {}),
        }),
      },
    ),
  /**
   * 위키 단순화 step5 — 특정 종목 "지금 재계산"(force). quarter 생략 시 서빙 분기 ∪ 배지가 보는
   * dirty 분기(>= served)·입력 0 분기 제외. force = dirty bump + 멱등 재계산(세대 single-winner CAS 보호).
   */
  recomputeWikiSymbol: (symbol: string, quarter?: string) =>
    request<{
      symbol: string;
      status: 'ok' | 'no-servable-quarter' | 'no-input';
      results: Array<{ quarter: string; committed: boolean; failed?: boolean; skipped?: string }>;
    }>('/admin/wiki-projections/recompute-symbol', {
      method: 'POST',
      body: JSON.stringify({ symbol, ...(quarter ? { quarter } : {}) }),
    }),
  /** 위키 단순화 step5 — fail ledger 조회(대시보드). stuck = 현 세대가 결정적 실패로 막힘. */
  wikiFails: () =>
    request<{ fails: WikiFail[] }>('/admin/wiki-projections/fails'),
};

export interface WikiFail {
  symbol: string;
  quarter: string;
  blockedSeq: number;
  retryable: boolean;
  error: string | null;
  attempt: number;
  failedAt: number;
  dirtySeq: number | null;
  builtSeq: number | null;
  stuck: boolean;
}
